/**
 * Firebase Hooks
 * 
 * Custom hooks for Firebase operations including authentication,
 * user management, and real-time data synchronization.
 */

import { useState, useEffect, useCallback } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from '@/contexts/AuthContext';
import {
  getOrCreateUser,
  listenToUser,
  updateUserProfile,
  updateUserPreferences,
  updateUserStats,
  updateLastSession
} from '@/services/userService';
import {
  FirebaseUser,
  CreateUserData,
  type FirebaseError
} from '@/types/firebase';

// Hook return types
interface UseFirebaseUserResult {
  firebaseUser: FirebaseUser | null;
  loading: boolean;
  error: FirebaseError | null;
  refreshUser: () => Promise<void>;
  updateProfile: (profile: Partial<FirebaseUser['profile']>) => Promise<boolean>;
  updatePreferences: (preferences: Partial<FirebaseUser['preferences']>) => Promise<boolean>;
  updateStats: (stats: Partial<FirebaseUser['overallStats']>) => Promise<boolean>;
  updateLastSession: (session: FirebaseUser['lastSession']) => Promise<boolean>;
}

/**
 * Hook for managing Firebase user data
 */
export const useFirebaseUser = (): UseFirebaseUserResult => {
  const { authUser, isAuthenticated } = useAuth();
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<FirebaseError | null>(null);

  /**
   * Initialize or get Firebase user data
   */
  const initializeUser = useCallback(async (user: User) => {
    setLoading(true);
    setError(null);

    try {
      console.log('🔥 Initializing Firebase user data:', {
        uid: user.uid,
        email: user.email,
        displayName: user.displayName
      });

      const userData: CreateUserData = {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        emailVerified: user.emailVerified,
        profile: {
          firstName: user.displayName?.split(' ')[0] || '',
          lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        }
      };

      console.log('📦 Creating user with data:', userData);
      const result = await getOrCreateUser(user.uid, userData);

      if (result.success && result.data) {
        setFirebaseUser(result.data);
        console.log('✅ Firebase user initialized successfully:', {
          uid: result.data.uid,
          email: result.data.email,
          createdAt: result.data.createdAt
        });
      } else {
        console.error('❌ User initialization failed:', result.error);
        throw new Error(result.error?.message || 'Failed to initialize user');
      }
    } catch (err) {
      console.error('🚫 Failed to initialize Firebase user:', err);
      const firebaseError: FirebaseError = {
        code: 'user/initialization-failed',
        message: err instanceof Error ? err.message : 'Unknown error'
      };
      setError(firebaseError);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Set up real-time user data listener
   */
  useEffect(() => {
    if (!isAuthenticated || !authUser) {
      setFirebaseUser(null);
      setError(null);
      return;
    }

    console.log('👂 Setting up Firebase user listener:', authUser.uid);

    // Initialize user first
    const firebaseAuthUser = {
      uid: authUser.uid,
      email: authUser.email,
      displayName: authUser.displayName,
      photoURL: authUser.photoURL,
      emailVerified: authUser.emailVerified
    } as User;

    initializeUser(firebaseAuthUser);

    // Set up real-time listener
    const unsubscribe = listenToUser(
      authUser.uid,
      (userData, error) => {
        if (error) {
          console.error('🚫 User listener error:', error);
          setError(error);
        } else if (userData) {
          console.log('🔄 Firebase user data updated');
          setFirebaseUser(userData);
          setError(null);
        }
      }
    );

    return unsubscribe;
  }, [isAuthenticated, authUser, initializeUser]);

  /**
   * Refresh user data manually
   */
  const refreshUser = useCallback(async () => {
    if (!authUser) return;

    const firebaseAuthUser = {
      uid: authUser.uid,
      email: authUser.email,
      displayName: authUser.displayName,
      photoURL: authUser.photoURL,
      emailVerified: authUser.emailVerified
    } as User;

    await initializeUser(firebaseAuthUser);
  }, [authUser, initializeUser]);

  /**
   * Update user profile
   */
  const updateProfile = useCallback(async (
    profile: Partial<FirebaseUser['profile']>
  ): Promise<boolean> => {
    if (!authUser) return false;

    try {
      const result = await updateUserProfile(authUser.uid, profile);
      if (!result.success) {
        setError(result.error || null);
        return false;
      }
      return true;
    } catch (error) {
      console.error('🚫 Failed to update profile:', error);
      setError({
        code: 'user/profile-update-failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      return false;
    }
  }, [authUser]);

  /**
   * Update user preferences
   */
  const updatePreferences = useCallback(async (
    preferences: Partial<FirebaseUser['preferences']>
  ): Promise<boolean> => {
    if (!authUser) return false;

    try {
      const result = await updateUserPreferences(authUser.uid, preferences);
      if (!result.success) {
        setError(result.error || null);
        return false;
      }
      return true;
    } catch (error) {
      console.error('🚫 Failed to update preferences:', error);
      setError({
        code: 'user/preferences-update-failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      return false;
    }
  }, [authUser]);

  /**
   * Update user statistics
   */
  const updateStats = useCallback(async (
    stats: Partial<FirebaseUser['overallStats']>
  ): Promise<boolean> => {
    if (!authUser) return false;

    try {
      const result = await updateUserStats(authUser.uid, stats);
      if (!result.success) {
        setError(result.error || null);
        return false;
      }
      return true;
    } catch (error) {
      console.error('🚫 Failed to update stats:', error);
      setError({
        code: 'user/stats-update-failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      return false;
    }
  }, [authUser]);

  /**
   * Update last session info
   */
  const updateLastSessionInfo = useCallback(async (
    session: FirebaseUser['lastSession']
  ): Promise<boolean> => {
    if (!authUser) return false;

    try {
      const result = await updateLastSession(authUser.uid, session);
      if (!result.success) {
        setError(result.error || null);
        return false;
      }
      return true;
    } catch (error) {
      console.error('🚫 Failed to update last session:', error);
      setError({
        code: 'user/last-session-update-failed',
        message: error instanceof Error ? error.message : 'Unknown error'
      });
      return false;
    }
  }, [authUser]);

  return {
    firebaseUser,
    loading,
    error,
    refreshUser,
    updateProfile,
    updatePreferences,
    updateStats,
    updateLastSession: updateLastSessionInfo
  };
};

/**
 * Hook for Firebase connection status
 */
export const useFirebaseStatus = () => {
  const [status, setStatus] = useState({
    initialized: false,
    connected: false,
    error: null as string | null
  });

  useEffect(() => {
    const checkStatus = async () => {
      try {
        // Import dynamically to avoid circular dependencies
        const { getFirebaseStatus } = await import('@/firebase');
        const firebaseStatus = await getFirebaseStatus();
        
        setStatus({
          initialized: firebaseStatus.initialized,
          connected: firebaseStatus.connected,
          error: null
        });
      } catch (error) {
        console.error('🚫 Firebase status check failed:', error);
        setStatus({
          initialized: false,
          connected: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    };

    checkStatus();
    
    // Check status every 30 seconds
    const interval = setInterval(checkStatus, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return status;
};

/**
 * Hook for offline data handling
 */
export const useOfflineData = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingOperations, setPendingOperations] = useState<string[]>([]);

  useEffect(() => {
    const handleOnline = () => {
      console.log('🌐 App is online');
      setIsOnline(true);
      // TODO: Sync pending operations
    };

    const handleOffline = () => {
      console.log('📱 App is offline');
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const addPendingOperation = useCallback((operationId: string) => {
    setPendingOperations(prev => [...prev, operationId]);
  }, []);

  const removePendingOperation = useCallback((operationId: string) => {
    setPendingOperations(prev => prev.filter(id => id !== operationId));
  }, []);

  return {
    isOnline,
    pendingOperations,
    addPendingOperation,
    removePendingOperation
  };
};

// Development helpers
if (import.meta.env.DEV) {
  console.log('🔥 Firebase hooks initialized in development mode');
}
